import { List, Map } from 'immutable';
import { IImmutableMap } from 'messagetypes/immutable-map';

export enum RevisionView {
    ACTIVITY_FEED = 'ACTIVITY_FEED',
    SELECT_STRAND = 'SELECT_STRAND',
    STRAND = 'STRAND',
    TOPIC = 'TOPIC',
}

// RevisionPageState stores the page that the student is currently on within revision and state
// specific to that page. For example, the strand page needs to know which strand is selected.
export type RevisionPageState =
    | {
          page: RevisionView.SELECT_STRAND;
      }
    | {
          page: RevisionView.ACTIVITY_FEED;
      }
    | {
          page: RevisionView.STRAND;
          strandName: string;
      }
    | {
          page: RevisionView.TOPIC;
          topicID: string;
          // Can select this topic at a different stream to the default
          learningPathID?: string;
      };

export const getRevisionViewID = (page: RevisionPageState): string => {
    switch (page.page) {
        case RevisionView.TOPIC:
            return `${RevisionView.TOPIC}/${page.topicID}`;
        case RevisionView.STRAND:
            return `${RevisionView.STRAND}/${page.strandName}`;
        case RevisionView.SELECT_STRAND:
        case RevisionView.ACTIVITY_FEED:
            // Both of these views should share a view ID so it is possible to
            // pop back to whichever was last using the breadcrumb.
            return 'REVISION_HOME_PAGE';
    }
};

// PageChangeAction represents the actions that cause the page to change within revision which are
// reported in analytics.
export enum PageChangeAction {
    CLICK_STRAND = 'CLICK STRAND',
    CLICK_TOPIC = 'CLICK TOPIC',
    CLICK_BREADCRUMB = 'CLICK BREADCRUMB',
    CLICK_TAB = 'CLICK TAB',
    CLICK_TOPIC_LINK = 'CLICK_TOPIC_LINK',
}

// SubstrandsOpenMap stores (for each strand) whether each substrand has been selected and is open.
export type SubstrandsOpenMap = {
    [strandName: string]: SubstrandsOpenState;
};

// SubstrandsOpenState is the strand specific state within SubstrandsOpenMap.
export type SubstrandsOpenState = {
    [substrandName: string]: boolean | undefined;
};

// RevisionViewState contains the revision topic browser state that is persisted to viewstate.
// This is the state that needs to persist when the topic browser is unmounted and mounted again.
export type RevisionViewState = {
    page: RevisionPageState;
    substrandsOpen?: SubstrandsOpenMap;
};
export type RevisionViewStateImmutable = IImmutableMap<RevisionViewState>;

// Revision stream contains information about a stream in revision.
export type RevisionStream = {
    // name is the name of the stream which is displayed to the student
    name: string;
    // annotation is some text displayed alongside the stream in the stream selection
    // drop-down, e.g. "(hardest)"
    annotation?: string;
    // serverStream is the name used to refer to the stream on the server, used to
    // convert from the client enum to the name the server understands
    serverStream: string;
    // curriculum is the key of the curriculum this stream is for, e.g. 'gcse' or 'ks3'
    curriculum: string;
    // level is the curriculum level key
    level: string;
};

export type SelectTopicFunc = (args: {
    strandName?: string;
    substrandName?: string;
    topicID: string;
    // optional, omitted will assume selected stream
    learningPathID?: string;
}) => void;

// Activity feed types

export type ActivityFeedWork = IImmutableMap<{
    learningUnitID: string;
    curriculumTopicName?: string;
    packageType: string;
    latestAttempt: string; // timestamp
    attempts: number;
    correctAnswers: number;
}>;

export type ActivityFeedDay = IImmutableMap<{
    date: string;
    work: List<ActivityFeedWork>;
}>;

export type ActivityFeedLU = IImmutableMap<{
    title: string;
}>;

export type ActivityFeedData = IImmutableMap<{
    days: List<ActivityFeedDay>;
    learningUnits: Map<string, ActivityFeedLU>;
}>;
